import { differenceInDays, format } from 'date-fns'

import { track, TravelSearchInitialize, TravelSearchOptions, TravelSearchReset, TravelSearchSubmit } from '@dfds-frontend/tracking'
import { mapUnloRoute, normalizeRouteCodeToUnlocode } from '@dfds-pax/utils'

import { isSogRoute } from './sog-helpers'

import { selectedDates } from '../Hooks/dates/traveldates-types'
import { Route } from '../Hooks/routes/route-types'
import { SelectedPassengers, SelectedVehicle, VehicleTypesResponse } from '../Hooks/vehicles/vehicle-types'

function setTrackingSession() {
  sessionStorage.setItem('TravelSearchTracking', crypto.randomUUID())
}

export function getTrackingSession() {
  const id = sessionStorage.getItem('TravelSearchTracking')
  return id === null ? undefined : id
}

// Cached /routes responses from before the UN/LOCODE migration may still contain seabook route codes
const toSeabookRoute = (routeCode: string) => mapUnloRoute(normalizeRouteCodeToUnlocode(routeCode) ?? routeCode)

export function TrackRoute(selectedRoute: Route, vehicleApiData: VehicleTypesResponse) {
  const payload: IRouteUpdate = {
    is_pets_allowed_for_foot_passenger: vehicleApiData?.isPetsAllowedForFootPassenger,
    is_vehicle_mandatory: vehicleApiData?.isVehicleMandatory,
    max_number_of_pets: vehicleApiData?.maxNumberOfPets,
    route_code: toSeabookRoute(selectedRoute.routeCode),
    route_name: selectedRoute.name,
    route_owner: selectedRoute.salesOwner,
  }
  track<TravelSearchOptions>({
    event: 'travel_search_options_route',
    travel_search_session: getTrackingSession(),
    ...payload,
  })
}

export function TrackMultipleVehicles(isMultipleVehicle: boolean) {
  track<TravelSearchOptions>({
    event: 'travel_search_options_multiple_vehicles',
    is_multiple_vehicles: isMultipleVehicle,
    travel_search_session: getTrackingSession(),
  })
}

export function TrackVehicle(selectedVehicle: SelectedVehicle, vehicleApiData: VehicleTypesResponse) {
  const payload: IVehicleUpdate = {
    vehicle_category_code: vehicleApiData?.vehicleTypes.find((vt) => vt.code === selectedVehicle?.code)?.categoryCode || 'unknown',
    vehicle_code: selectedVehicle?.code,
    vehicle_count: selectedVehicle?.count,
    vehicle_is_pets_allowed: vehicleApiData?.isPetsAllowedForFootPassenger,
    vehicle_trailer_code: selectedVehicle?.trailer?.code ?? '',
  }
  track<TravelSearchOptions>({
    event: 'travel_search_options_vehicle',
    travel_search_session: getTrackingSession(),
    ...payload,
  })
}

export function TrackPassengers(selectedPassengers: SelectedPassengers) {
  const payload: IPassengerUpdate = {
    passenger_adults: selectedPassengers.adults,
    passenger_children: selectedPassengers.children,
    passenger_pets: selectedPassengers.pets,
    passenger_total: selectedPassengers.adults + selectedPassengers.children,
  }
  track<TravelSearchOptions>({
    event: 'travel_search_options_passenger',
    travel_search_session: getTrackingSession(),
    ...payload,
  })
}

export function TrackDates(selectedDates: selectedDates, oneWay: boolean) {
  if (selectedDates.outboundDate) {
    const payload: IDateUpdate = {
      departure_date: format(selectedDates.outboundDate, 'yyyy-MM-dd'),
      return_date: !oneWay && selectedDates.homeboundDate ? format(selectedDates.homeboundDate, 'yyyy-MM-dd') : '',
      trip_duration:
        !oneWay && selectedDates.outboundDate && selectedDates.homeboundDate
          ? differenceInDays(selectedDates.outboundDate, selectedDates.homeboundDate)
          : 0,
    }
    track<TravelSearchOptions>({
      event: 'travel_search_options_date',
      travel_search_session: getTrackingSession(),
      ...payload,
    })
  }
}

export function TrackOneWay(oneWay: boolean) {
  const payload: ITripType = {
    trip_type: oneWay ? 'oneway' : 'return',
  }
  track<TravelSearchOptions>({
    event: 'travel_search_options_trip_type',
    travel_search_session: getTrackingSession(),
    ...payload,
  })
}

export function TrackDaytrip(isDayTrip: boolean) {
  if (!isDayTrip) return // Do not track daytrip if it is not selected
  const payload: ITripType = {
    trip_type: 'daytrip',
  }
  track<TravelSearchOptions>({
    event: 'travel_search_options_trip_type',
    travel_search_session: getTrackingSession(),
    ...payload,
  })
}

function TravelSearchPayload(
  selectedRoute: Route,
  selectedDates: selectedDates,
  selectedVehicle: SelectedVehicle,
  vehicleApiData: VehicleTypesResponse,
  selectedPassengers: SelectedPassengers,
  oneWay: boolean
) {
  const payload: ITravelSearchTracking = {
    departure_date: selectedDates.outboundDate ? format(selectedDates.outboundDate, 'yyyy-MM-dd') : '',
    is_pets_allowed_for_foot_passenger: vehicleApiData?.isPetsAllowedForFootPassenger,
    is_vehicle_mandatory: vehicleApiData?.isVehicleMandatory,
    max_number_of_pets: vehicleApiData?.maxNumberOfPets,
    passenger_adults: selectedPassengers.adults,
    passenger_children: selectedPassengers.children,
    passenger_pets: selectedPassengers.pets,
    passenger_total: selectedPassengers.adults + selectedPassengers.children,
    return_date: !oneWay && selectedDates.homeboundDate ? format(selectedDates.homeboundDate, 'yyyy-MM-dd') : '',
    route_code: toSeabookRoute(selectedRoute.routeCode),
    route_name: selectedRoute.name,
    route_owner: selectedRoute.salesOwner,
    trip_duration:
      !oneWay && selectedDates.outboundDate && selectedDates.homeboundDate
        ? differenceInDays(selectedDates.outboundDate, selectedDates.homeboundDate)
        : 0,
    trip_type: oneWay ? 'oneway' : 'return',
    vehicle_category_code: 'unknown',
    vehicle_code: selectedVehicle.code,
    vehicle_count: selectedVehicle.count,
    vehicle_is_pets_allowed: vehicleApiData?.isPetsAllowedForFootPassenger,
    vehicle_trailer_code: selectedVehicle.trailer?.code ?? '',
  }
  return payload
}

function TravelSearchFRSPayload(selectedRoute: Route, selectedDates: selectedDates, oneWay: boolean, selectedCurrency?: string) {
  const payload: ITravelSearchFRSTracking = {
    currency: selectedCurrency || '',
    departure_date: selectedDates.outboundDate ? format(selectedDates.outboundDate, 'yyyy-MM-dd') : '',
    return_date: !oneWay && selectedDates.homeboundDate ? format(selectedDates.homeboundDate, 'yyyy-MM-dd') : '',
    route_code: toSeabookRoute(selectedRoute.routeCode),
    route_name: selectedRoute.name,
    route_owner: selectedRoute.salesOwner,
    trip_duration:
      !oneWay && selectedDates.outboundDate && selectedDates.homeboundDate
        ? differenceInDays(selectedDates.outboundDate, selectedDates.homeboundDate)
        : 0,
    trip_type: oneWay ? 'oneway' : 'return',
  }
  return payload
}
export function TrackingCommand(
  event: TravelSearchTrackingTypes,
  selectedRoute?: Route,
  selectedDates?: selectedDates,
  selectedVehicle?: SelectedVehicle,
  vehicleApiData?: VehicleTypesResponse,
  selectedPassengers?: SelectedPassengers,
  selectedCurrency?: string,
  oneWay?: boolean
) {
  let payload: ITravelSearchTracking | ITravelSearchFRSTracking | undefined

  if (selectedRoute && isSogRoute(selectedRoute.routeCode) && selectedDates && oneWay !== undefined && selectedCurrency) {
    payload = TravelSearchFRSPayload(selectedRoute, selectedDates, oneWay, selectedCurrency)
  } else if (
    selectedRoute &&
    !isSogRoute(selectedRoute.routeCode) &&
    selectedDates &&
    selectedVehicle &&
    vehicleApiData &&
    selectedPassengers &&
    oneWay !== undefined
  ) {
    payload = TravelSearchPayload(selectedRoute, selectedDates, selectedVehicle, vehicleApiData, selectedPassengers, oneWay)
  } else return

  const travelSearchSession = getTrackingSession()
  if (event === TravelSearchTrackingTypes.INITIALIZE && !travelSearchSession) {
    setTrackingSession()
    track<TravelSearchInitialize>({
      event: 'travel_search_initialize',
      travel_search_session: getTrackingSession(),
      ...payload,
    })
  } else if (event === TravelSearchTrackingTypes.SUBMIT && travelSearchSession) {
    track<TravelSearchSubmit>({
      event: 'travel_search_submit',
      travel_search_session: travelSearchSession,
      ...payload,
    })
  } else if (event === TravelSearchTrackingTypes.RESET && travelSearchSession) {
    track<TravelSearchReset>({
      event: 'travel_search_reset',
      travel_search_session: travelSearchSession,
      ...payload,
    })
  }
}

export enum TravelSearchTrackingTypes {
  INITIALIZE = 'init',
  SUBMIT = 'submit',
  RESET = 'reset',
}
interface ITravelSearchTracking {
  departure_date: string
  is_pets_allowed_for_foot_passenger: boolean
  is_vehicle_mandatory: boolean
  max_number_of_pets: number
  passenger_adults: number
  passenger_children: number
  passenger_pets: number
  passenger_total: number
  return_date: string
  route_code: string
  route_name: string
  route_owner: number
  trip_duration: number
  trip_type: 'oneway' | 'return'
  vehicle_category_code: string
  vehicle_code: string
  vehicle_count: number
  vehicle_is_pets_allowed: boolean
  vehicle_trailer_code: string
}

interface ITravelSearchFRSTracking {
  currency: string
  departure_date: string
  return_date: string
  route_code: string
  route_name: string
  route_owner: number
  trip_duration: number
  trip_type: 'oneway' | 'return'
}

interface ITripType {
  trip_type: 'oneway' | 'return' | 'daytrip'
}

interface IRouteUpdate {
  is_pets_allowed_for_foot_passenger: boolean
  is_vehicle_mandatory: boolean
  max_number_of_pets: number
  route_code: string
  route_name: string
  route_owner: number
}

interface IVehicleUpdate {
  vehicle_category_code: string
  vehicle_code: string
  vehicle_count: number
  vehicle_is_pets_allowed: boolean
  vehicle_trailer_code: string
}

interface IPassengerUpdate {
  passenger_adults: number
  passenger_children: number
  passenger_pets: number
  passenger_total: number
}

interface IDateUpdate {
  departure_date: string
  return_date: string
  trip_duration: number
}
